import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import {
  computeReitMetrics,
} from '@/lib/utils/utils.reits';
import {
  useCreateReitHolding,
  useUpdateReitHolding,
  useDeleteReitHolding,
  useFetchReitDividendPayments,
  useDeleteReitDividendPayment,
  useCreateReitDeposit,
  useRecordReitDividend,
  useFetchReitHoldings,
  useFetchReitTypes,
  useFetchReitSectors
} from '@/hooks/reits';
import { useFetchAccounts } from '@accountHooks';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Plus,
  Edit,
  Trash2,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Building2,
  Percent,
  ChevronDown,
  ChevronUp,
  Info,
  Search,
  ArrowDownToLine,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import REITForm from '@/components/reit-form/REITForm';
import REITDividendForm from '@/components/reit-dividend-form/REITDividendForm';
import REITDepositForm from '@/components/reit-deposit-form/REITDepositForm';
import { PageHeaderSkeleton, AccountCardSkeleton } from '@/components/LoadingSkeletons';
import {ReitHolding} from "@/@types/reit.models.ts";
import {CreateReitHoldingDto, CreateReitDepositDto, RecordREITDividendDto} from "@/@types/reit.dto";

/**
 * REITsPage displays and manages REIT holdings and dividend payment history.
 * Accessible at /investments/reits.
 */
export default function REITsPage() {
  const [formOpen, setFormOpen] = useState(false);
  const [dividendFormOpen, setDividendFormOpen] = useState(false);
  const [depositFormOpen, setDepositFormOpen] = useState(false);
  const [editingHolding, setEditingHolding] = useState<ReitHolding | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [expandedDividends, setExpandedDividends] = useState<string | null>(null);
  const [preselectedHoldingId, setPreselectedHoldingId] = useState<string | undefined>();
  const [preselectedDepositHoldingId, setPreselectedDepositHoldingId] = useState<string | undefined>();
  const [statusMessage, setStatusMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const navigate = useNavigate();

  const { data: holdings = [], isLoading, error, refetch } = useFetchReitHoldings();
  const { data: allDividends = [] } = useFetchReitDividendPayments();
  const { data: accounts = [] } = useFetchAccounts();

  const { data: reitTypes = [] } = useFetchReitTypes();
  const { data: reitSectors = [] } = useFetchReitSectors();

  const typeLabels = useMemo(() => {
    const map = new Map<string, string>();
    reitTypes.forEach(t => map.set(t.name, t.description));
    return map;
  }, [reitTypes]);

  const sectorLabels = useMemo(() => {
    const map = new Map<string, string>();
    reitSectors.forEach(s => map.set(s.name, s.description));
    return map;
  }, [reitSectors]);

  const createHolding = useCreateReitHolding();
  const updateHolding = useUpdateReitHolding();
  const deleteHolding = useDeleteReitHolding();
  const createDeposit = useCreateReitDeposit();
  const recordDividend = useRecordReitDividend();
  const deleteDividend = useDeleteReitDividendPayment();

  const filteredHoldings = useMemo(() => {
    if (!searchQuery) return holdings;
    const lowerQuery = searchQuery.toLowerCase();
    return holdings.filter(h => 
      h.reit_name?.toLowerCase().includes(lowerQuery) ||
      h.ticker_symbol?.toLowerCase().includes(lowerQuery) ||
      h.reit_type?.toLowerCase().includes(lowerQuery) ||
      h.sector?.toLowerCase().includes(lowerQuery)
    );
  }, [holdings, searchQuery]);

  // Portfolio-level metrics
  const portfolioMetrics = filteredHoldings.reduce(
    (acc, h) => {
      const m = computeReitMetrics(h);
      acc.totalValue += m.marketValue;
      acc.totalCost += m.costBasis;
      acc.totalAnnualIncome += m.annualIncome;
      return acc;
    },
    { totalValue: 0, totalCost: 0, totalAnnualIncome: 0 }
  );
  const totalUnrealizedGL = portfolioMetrics.totalValue - portfolioMetrics.totalCost;
  const portfolioYield =
    portfolioMetrics.totalValue > 0
      ? (portfolioMetrics.totalAnnualIncome / portfolioMetrics.totalValue) * 100
      : 0;
  const totalDividendsReceived = allDividends.reduce((sum, d) => sum + d.total_amount, 0);

  const handleSaveHolding = async (data: CreateReitHoldingDto) => {
    try {
      if (editingHolding) {
        await updateHolding.mutateAsync({ id: editingHolding.id, ...data });
        toast.success('REIT holding updated');
        setStatusMessage('REIT holding updated');
      } else {
        await createHolding.mutateAsync(data);
        toast.success('REIT holding added');
        setStatusMessage('REIT holding added');
      }
      setFormOpen(false);
      setEditingHolding(null);
    } catch (err) {
      const msg = editingHolding ? 'Failed to update REIT holding' : 'Failed to add REIT holding';
      toast.error(msg);
      setStatusMessage(msg);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingId) return;
    try {
      await deleteHolding.mutateAsync(deletingId);
      toast.success('REIT holding deleted');
      setStatusMessage('REIT holding deleted');
      setDeletingId(null);
    } catch {
      toast.error('Failed to delete REIT holding');
    }
  };

  const handleSaveDividend = async (data: RecordREITDividendDto) => {
    try {
      await recordDividend.mutateAsync(data);
      toast.success('Dividend payment recorded');
      setStatusMessage('Dividend payment recorded');
      setDividendFormOpen(false);
      setPreselectedHoldingId(undefined);
    } catch {
      toast.error('Failed to record dividend payment');
    }
  };

  const handleSaveDeposit = async (data: CreateReitDepositDto) => {
    try {
      await createDeposit.mutateAsync(data);
      toast.success('REIT deposit recorded');
      setStatusMessage('REIT deposit recorded');
      setDepositFormOpen(false);
      setPreselectedDepositHoldingId(undefined);
    } catch {
      toast.error('Failed to record REIT deposit');
    }
  };

  const handleDeleteDividend = async (id: string) => {
    try {
      await deleteDividend.mutateAsync(id);
      toast.success('Dividend payment deleted');
    } catch {
      toast.error('Failed to delete dividend payment');
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6" aria-busy="true" aria-live="polite">
        <PageHeaderSkeleton />
        <div className="grid gap-4 grid-cols-2 sm:grid-cols-4">
          {[1, 2, 3, 4].map(i => <AccountCardSkeleton key={i} />)}
        </div>
        <AccountCardSkeleton />
        <AccountCardSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <p className="text-destructive">Failed to load REIT holdings.</p>
        <Button variant="outline" onClick={() => refetch()}>Retry</Button>
      </div>
    );
  }

  const hasHoldings = holdings.length > 0;

  return (
    <ErrorBoundary onReset={() => window.location.reload()}>
      <div className="space-y-6">
        {/* SR live region */}
        <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
          {statusMessage}
        </div>

        {/* Header */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">REITs</h1>
            <p className="text-sm text-muted-foreground sm:text-base">
              Track your Real Estate Investment Trust holdings and dividend income
            </p>
          </div>

          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:flex-wrap w-full lg:w-auto">
            <div className="flex flex-col gap-2 sm:flex-row w-full sm:w-auto" role="group" aria-label="REIT actions">
              <Button
                variant="outline"
                onClick={() => { setPreselectedHoldingId(undefined); setDividendFormOpen(true); }}
                disabled={!hasHoldings}
                aria-disabled={!hasHoldings}
                className="min-h-[44px] w-full sm:w-auto"
              >
                <DollarSign className="mr-2 h-4 w-4" aria-hidden="true" />
                Record Dividend
              </Button>
              <Button
                variant="outline"
                onClick={() => { setPreselectedDepositHoldingId(undefined); setDepositFormOpen(true); }}
                disabled={!hasHoldings}
                className="min-h-[44px] w-full sm:w-auto"
              >
                <ArrowDownToLine className="mr-2 h-4 w-4" aria-hidden="true" />
                Deposit
              </Button>
              <Button
                onClick={() => { setEditingHolding(null); setFormOpen(true); }}
                className="min-h-[44px] w-full sm:w-auto"
                aria-label="Add new REIT holding"
              >
                <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                Add
              </Button>
            </div>
          </div>
        </div>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search REITs..."
              className="pl-8"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        {/* Portfolio Metrics */}
        {hasHoldings && (
          <div className="grid gap-4 grid-cols-2 sm:grid-cols-4">
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground mb-1">Portfolio Value</p>
                <p className="text-lg font-bold">
                  {portfolioMetrics.totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground mb-1">Unrealized G/L</p>
                <p className={`text-lg font-bold ${totalUnrealizedGL >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {totalUnrealizedGL >= 0 ? '+' : ''}
                  {totalUnrealizedGL.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground mb-1">Annual Income</p>
                <p className="text-lg font-bold">
                  {portfolioMetrics.totalAnnualIncome.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground mb-1">Portfolio Yield</p>
                <p className="text-lg font-bold">{portfolioYield.toFixed(2)}%</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Holdings List */}
        {hasHoldings ? (
          <div className="space-y-4" role="list" aria-label="REIT holdings">
            {filteredHoldings.map(holding => {
              const m = computeReitMetrics(holding);
              const isGain = m.unrealizedGainLoss >= 0;
              const holdingDividends = allDividends.filter(d => d.reit_holding_id === holding.id);
              const isExpanded = expandedDividends === holding.id;

              return (
                <Card
                  key={holding.id}
                  className="hover:bg-accent/50 transition-colors cursor-pointer"
                  role="listitem"
                  onClick={() => navigate(`/investments/reits/${holding.id}`)}
                >
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex flex-col gap-4">
                      {/* Header */}
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <h3 className="text-lg font-semibold">{holding.reit_name}</h3>
                            {holding.ticker_symbol && (
                              <span className="text-sm text-muted-foreground">({holding.ticker_symbol})</span>
                            )}
                            <Badge variant="secondary">
                              <div className="flex items-center gap-2 capitalize">
                                {holding.reit_type}
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Info className="h-3 w-3 text-muted-foreground" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p className="max-w-xs">
                                        {typeLabels.get(holding.reit_type) ?? holding.reit_type}
                                      </p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                            </Badge>
                            <Badge variant="outline">
                              <div className="flex items-center gap-2 capitalize">
                                {holding.sector}
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Info className="h-3 w-3 text-muted-foreground" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p className="max-w-xs">
                                        {sectorLabels.get(holding.sector) ?? holding.sector}
                                      </p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Purchased {new Date(holding.purchase_date).toLocaleDateString()} · {holding.dividend_frequency} dividends
                          </p>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPreselectedHoldingId(holding.id);
                              setDividendFormOpen(true);
                            }}
                            aria-label={`Record dividend for ${holding.reit_name}`}
                            className="min-h-[44px] min-w-[44px]"
                          >
                            <DollarSign className="h-4 w-4" aria-hidden="true" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPreselectedDepositHoldingId(holding.id);
                              setDepositFormOpen(true);
                            }}
                            aria-label={`Deposit to ${holding.reit_name}`}
                            className="min-h-[44px] min-w-[44px]"
                          >
                            <ArrowDownToLine className="h-4 w-4" aria-hidden="true" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => { e.stopPropagation(); setEditingHolding(holding); setFormOpen(true); }}
                            aria-label={`Edit ${holding.reit_name}`}
                            className="min-h-[44px] min-w-[44px]"
                          >
                            <Edit className="h-4 w-4" aria-hidden="true" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => { e.stopPropagation(); setDeletingId(holding.id); }}
                            aria-label={`Delete ${holding.reit_name}`}
                            className="min-h-[44px] min-w-[44px]"
                          >
                            <Trash2 className="h-4 w-4" aria-hidden="true" />
                          </Button>
                        </div>
                      </div>

                      {/* Metrics Grid */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Units Held</p>
                          <p className="text-sm font-medium">{holding.units_held.toLocaleString(undefined, { maximumFractionDigits: 4 })}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Market Value</p>
                          <p className="text-sm font-medium">
                            {holding.currency} {m.marketValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Unrealized G/L</p>
                          <div className="flex items-center gap-1">
                            <p className={`text-sm font-medium ${isGain ? 'text-emerald-600' : 'text-red-600'}`}>
                              {isGain ? '+' : ''}{holding.currency} {m.unrealizedGainLoss.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                            {isGain
                              ? <TrendingUp className="h-3 w-3 text-emerald-600" aria-hidden="true" />
                              : <TrendingDown className="h-3 w-3 text-red-600" aria-hidden="true" />}
                          </div>
                          <p className={`text-xs ${isGain ? 'text-emerald-600' : 'text-red-600'}`}>
                            {isGain ? '+' : ''}{m.unrealizedGainLossPct.toFixed(2)}%
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Dividend Yield</p>
                          <div className="flex items-center gap-1">
                            <Percent className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
                            <p className="text-sm font-medium">{m.dividendYield.toFixed(2)}%</p>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {holding.currency} {m.annualIncome.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} / yr
                          </p>
                        </div>
                      </div>

                      {/* Optional metrics row */}
                      {(holding.nav_per_unit || holding.occupancy_rate) && (
                        <div className="flex flex-wrap gap-4 text-xs text-muted-foreground border-t pt-3">
                          {holding.nav_per_unit && (
                            <span>NAV/unit: {holding.currency} {holding.nav_per_unit.toFixed(4)}</span>
                          )}
                          {holding.occupancy_rate && (
                            <span>Occupancy: {holding.occupancy_rate.toFixed(1)}%</span>
                          )}
                        </div>
                      )}

                      {/* Dividend history toggle */}
                      {holdingDividends.length > 0 && (
                        <div className="border-t pt-3">
                          <button
                            type="button"
                            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                            onClick={(e) => { e.stopPropagation(); setExpandedDividends(isExpanded ? null : holding.id); }}
                            aria-expanded={isExpanded}
                            aria-controls={`dividends-${holding.id}`}
                          >
                            {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                            {holdingDividends.length} dividend payment{holdingDividends.length !== 1 ? 's' : ''} recorded
                          </button>
                          {isExpanded && (
                            <div id={`dividends-${holding.id}`} className="mt-3 space-y-2">
                              {holdingDividends.map(d => (
                                <div key={d.id} className="flex items-center justify-between text-xs bg-muted/50 rounded px-3 py-2">
                                  <span>{new Date(d.payment_date).toLocaleDateString()}</span>
                                  <span>{d.currency} {d.amount_per_unit.toFixed(4)}/unit</span>
                                  <span className="font-medium">{d.currency} {d.total_amount.toFixed(2)}</span>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={() => handleDeleteDividend(d.id)}
                                    aria-label="Delete dividend payment"
                                  >
                                    <Trash2 className="h-3 w-3" aria-hidden="true" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card role="status" aria-label="Empty state">
            <CardContent className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <Building2 className="h-12 w-12 text-muted-foreground mb-4" aria-hidden="true" />
              <p className="text-base sm:text-lg text-muted-foreground mb-2">No REIT holdings yet</p>
              <p className="text-xs sm:text-sm text-muted-foreground mb-6">
                Add your first REIT to start tracking dividends, yield, and portfolio performance
              </p>
              <Button
                onClick={() => { setEditingHolding(null); setFormOpen(true); }}
                className="min-h-[44px] w-full sm:w-auto"
              >
                <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                Add REIT
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={formOpen} onOpenChange={open => { setFormOpen(open); if (!open) setEditingHolding(null); }}>
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[600px] w-[95vw] sm:w-full">
            <DialogHeader>
              <DialogTitle>{editingHolding ? 'Edit REIT Holding' : 'Add REIT Holding'}</DialogTitle>
              <DialogDescription>
                {editingHolding ? 'Update the details of your REIT holding.' : 'Add a new REIT to your portfolio.'}
              </DialogDescription>
            </DialogHeader>
            <REITForm
              holding={editingHolding}
              onSave={handleSaveHolding}
              onCancel={() => { setFormOpen(false); setEditingHolding(null); }}
            />
          </DialogContent>
        </Dialog>

        {/* Dividend Dialog */}
        <Dialog open={dividendFormOpen} onOpenChange={open => { setDividendFormOpen(open); if (!open) setPreselectedHoldingId(undefined); }}>
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[500px] w-[95vw] sm:w-full">
            <DialogHeader>
              <DialogTitle>Record Dividend Payment</DialogTitle>
              <DialogDescription>Log a dividend payment received from a REIT holding.</DialogDescription>
            </DialogHeader>
            <REITDividendForm
              holdings={holdings}
              accounts={accounts}
              preselectedHoldingId={preselectedHoldingId}
              onSave={handleSaveDividend}
              onCancel={() => { setDividendFormOpen(false); setPreselectedHoldingId(undefined); }}
            />
          </DialogContent>
        </Dialog>

        {/* Deposit Dialog */}
        <Dialog open={depositFormOpen} onOpenChange={open => { setDepositFormOpen(open); if (!open) setPreselectedDepositHoldingId(undefined); }}>
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[500px] w-[95vw] sm:w-full">
            <DialogHeader>
              <DialogTitle>Record REIT Deposit</DialogTitle>
              <DialogDescription>Purchase additional units in a REIT holding.</DialogDescription>
            </DialogHeader>
            <REITDepositForm
              holdings={holdings}
              accounts={accounts}
              preselectedHoldingId={preselectedDepositHoldingId}
              onSave={handleSaveDeposit}
              onCancel={() => { setDepositFormOpen(false); setPreselectedDepositHoldingId(undefined); }}
            />
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deletingId} onOpenChange={open => !open && setDeletingId(null)}>
          <AlertDialogContent className="w-[95vw] sm:w-full sm:max-w-[500px]">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete REIT holding?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete the holding and all associated dividend payment history. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex-col gap-2 sm:flex-row">
              <AlertDialogCancel className="min-h-[44px] w-full sm:w-auto">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmDelete}
                className="min-h-[44px] w-full sm:w-auto bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </ErrorBoundary>
  );
}
